// src/services/InventoryService.js

import apiClient from "./apiClient";
import {
  formatPathInventoryFields,
  normalizePathInventoryItem,
  normalizePathInventoryList,
} from "../utils/nameFormatUtils";

const API_URL = `/inventory`; // Use relative path since apiClient has baseURL

// Display-only fields enriched on the client (e.g. from invoice lookup).
// InventoryDTO does not accept these — Jackson rejects unknown fields.
const PATH_INVENTORY_CLIENT_ONLY_FIELDS = [
  "supplierName",
  "supplier_name",
  "invoiceNumber",
  "invoice_number",
  "fromName",
  "from_name",
];

const stripPathInventoryClientOnlyFields = (data = {}) => {
  if (!data || typeof data !== "object") return data;
  const cleaned = { ...data };
  PATH_INVENTORY_CLIENT_ONLY_FIELDS.forEach((field) => {
    delete cleaned[field];
  });
  return cleaned;
};

export const getAllInventory = async () => {
  // apiClient will automatically add organizationId as query param via interceptor
  const response = await apiClient.get(API_URL);
  return normalizePathInventoryList(response.data);
};

export const getInventoryById = async (id) => {
  // apiClient will automatically add organizationId as query param via interceptor
  const response = await apiClient.get(`${API_URL}/${id}`);
  return normalizePathInventoryItem(response.data);
};

export const createInventory = async (inventory) => {
  // Remove organizationId from inventory object if it exists (apiClient will add it as query param)
  const { organizationId, ...inventoryData } = inventory;
  const cleanedData = stripPathInventoryClientOnlyFields(inventoryData);
  
  // Log the request for debugging
  const storedUser = localStorage.getItem("user");
  let orgId = null;
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      orgId = user?.organizationId;
    } catch (e) {
      // ignore
    }
  }
  console.log(`[inventoryService] createInventory: organizationId=${orgId}, data:`, cleanedData);
  
  // apiClient will automatically add organizationId as query parameter via interceptor
  // (not in body, as Inventory DTO needs it as query param for POST to ensure correct table selection)
  const response = await apiClient.post(API_URL, formatPathInventoryFields(cleanedData));
  return normalizePathInventoryItem(response.data);
};

export const updateInventory = async (id, inventory) => {
  // Remove organizationId from inventory object if it exists (apiClient will add it as query param)
  const { organizationId, ...inventoryData } = inventory;
  const cleanedData = stripPathInventoryClientOnlyFields(inventoryData);
  
  // Log the request for debugging
  const storedUser = localStorage.getItem("user");
  let orgId = null;
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      orgId = user?.organizationId;
    } catch (e) {
      // ignore
    }
  }
  console.log(`[inventoryService] updateInventory: id=${id}, organizationId=${orgId}, data:`, cleanedData);
  
  // apiClient will automatically add organizationId as query parameter via interceptor
  // (not in body, as Inventory DTO doesn't accept it in request body for PUT requests)
  const response = await apiClient.put(`${API_URL}/${id}`, formatPathInventoryFields(cleanedData));
  return normalizePathInventoryItem(response.data);
};

export const deleteInventory = async (id) => {
  // apiClient will automatically add organizationId as query param via interceptor
  const response = await apiClient.delete(`${API_URL}/${id}`);
  return response.data;
};

// New method to get existing batch numbers by BOM ID
export const getExistingBatchNumbers = async (bomId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_URL}/batchNumbers/${bomId}`);
    return normalizePathInventoryList(response.data);
  } catch (error) {
    console.error("Error fetching existing batch numbers:", error);
    throw error;
  }
};

// Updated function to deduct inventory without quantity
export const deductInventory = async (testId) => {
  try {
    // Get organizationId for logging
    const storedUser = localStorage.getItem("user");
    let orgId = null;
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        orgId = user?.organizationId || localStorage.getItem("organizationId");
        if (orgId && typeof orgId === 'string') {
          orgId = parseInt(orgId, 10);
        }
      } catch (e) {
        // ignore
      }
    }
    
    // Validate testId
    const numericTestId = typeof testId === 'string' ? parseInt(testId, 10) : Number(testId);
    if (isNaN(numericTestId) || numericTestId <= 0) {
      throw new Error(`Invalid testId for inventory deduction: ${testId}. Must be a positive number.`);
    }
    
    console.log(`[inventoryService] deductInventory: testId=${numericTestId} (original: ${testId}), organizationId=${orgId}`);
    console.log(`[inventoryService] API URL: ${API_URL}/deduct/${numericTestId}`);
    
    // apiClient will automatically add organizationId via interceptor
    // For PUT requests to /inventory, organizationId is added as a query parameter (see apiClient.js line 197)
    const url = `${API_URL}/deduct/${numericTestId}`;
    console.log(`[inventoryService] Making PUT request to: ${url} with organizationId=${orgId} (via interceptor)`);
    console.log(`[inventoryService] Expected full URL: ${url}?organizationId=${orgId}`);
    
    const response = await apiClient.put(url, {});
    
    console.log(`[inventoryService] deductInventory response status:`, response.status);
    console.log(`[inventoryService] deductInventory response headers:`, response.headers);
    console.log(`[inventoryService] deductInventory response data:`, response.data);
    
    // Log request config if available (to verify organizationId was added)
    if (response.config) {
      const requestUrl = response.config.url || url;
      const requestParams = response.config.params || {};
      const fullRequestUrl = `${response.config.baseURL || ''}${requestUrl}${Object.keys(requestParams).length > 0 ? '?' + new URLSearchParams(requestParams).toString() : ''}`;
      console.log(`[inventoryService] Actual request made to: ${fullRequestUrl}`);
      console.log(`[inventoryService] Request params:`, requestParams);
    }
    
    // Log the full response for debugging
    if (response.data && typeof response.data === 'string') {
      console.log(`[inventoryService] Response message: ${response.data}`);
      // Check if response actually indicates success
      const responseLower = response.data.toLowerCase();
      if (!responseLower.includes('success') && !responseLower.includes('updated') && !responseLower.includes('deducted')) {
        console.warn(`[inventoryService] ⚠️ Response may not indicate successful deduction: ${response.data}`);
      } else {
        // Even if response says success, warn that backend might not have actually deducted
        console.warn(`[inventoryService] ⚠️ Backend returned success message, but please verify inventory was actually deducted in database.`);
        console.warn(`[inventoryService] ⚠️ If inventory quantity in 4_inventory table is not updated, this is a backend issue.`);
        console.warn(`[inventoryService] ⚠️ Backend endpoint /inventory/deduct/{testId} should:`);
        console.warn(`[inventoryService] ⚠️   1. Look up BOM entries for lab test ID ${numericTestId}`);
        console.warn(`[inventoryService] ⚠️   2. Deduct required quantities from each inventory item`);
        console.warn(`[inventoryService] ⚠️   3. Update 4_inventory table for organizationId ${orgId}`);
      }
    } else if (response.data) {
      console.log(`[inventoryService] Response data:`, JSON.stringify(response.data, null, 2));
      // Check if response has success indicators
      if (response.data.message) {
        const messageLower = response.data.message.toLowerCase();
        if (!messageLower.includes('success') && !messageLower.includes('updated') && !messageLower.includes('deducted')) {
          console.warn(`[inventoryService] ⚠️ Response message may not indicate successful deduction: ${response.data.message}`);
        } else {
          console.warn(`[inventoryService] ⚠️ Backend returned success message, but please verify inventory was actually deducted in database.`);
        }
      }
    } else {
      console.warn(`[inventoryService] ⚠️ No response data received from inventory deduction API`);
    }
    
    return normalizePathInventoryList(response.data);
  } catch (error) {
    console.error("Error deducting inventory:", error);
    console.error("Error details:", {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
      statusText: error?.response?.statusText
    });
    throw error;
  }
};